// A shared artifact, by link (Adam, 13 Sep 2026): what /artifact/:shareId
// shows. Read without a login, the same way a shared dashboard is: the
// server's /shared/artifact routes need no Authorization header. A signed-in
// Manager's token (axios carries it once the app has set it) also gets the
// artifact's customer and job when Discuss is on; nobody else does.
import { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { API_BASE_URL } from '../constants/system'

export interface SharedArtifact {
  /** Where the page frames a shared web app; empty for anything else (Adam, 14 Sep). */
  app_url?: string
  share_id: string
  file_name: string
  title: string
  description: string
  mime_type: string
  file_size: number
  artifact_type: string
  is_dir: boolean
  status: string
  /** The customer's label from their skin ("Sky"); '' when unknown. */
  site_name: string
  /** Relative, no login needed. */
  download_url: string
  /** The sharer allowed signed-in viewers to discuss the page with Douglas (Adam, 16 Sep). */
  discuss_enabled?: boolean
  /** The artifact's customer and job; served only when discussion is on (Douglas plan T17). */
  customer?: string
  job?: string
}

export type SharedArtifactState =
  | { status: 'loading' }
  | { status: 'loaded'; artifact: SharedArtifact }
  | { status: 'not_found' }
  | { status: 'error'; message: string }

export default function useSharedArtifact(shareId: string | undefined): SharedArtifactState {
  const [state, setState] = useState<SharedArtifactState>({ status: 'loading' })
  const loadedFor = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (!shareId || loadedFor.current === shareId) return
    loadedFor.current = shareId
    let cancelled = false
    axios
      .get<SharedArtifact>(`${API_BASE_URL}/shared/artifact/${encodeURIComponent(shareId)}`)
      .then((res) => {
        if (!cancelled) setState({ status: 'loaded', artifact: res.data })
      })
      .catch((err) => {
        if (cancelled) return
        if (axios.isAxiosError(err) && err.response?.status === 404) setState({ status: 'not_found' })
        else setState({ status: 'error', message: err instanceof Error ? err.message : 'Failed to load' })
      })
    return () => {
      cancelled = true
    }
  }, [shareId])

  if (!shareId) return { status: 'not_found' }
  return state
}

/** The kinds the public page can show inline, and how. */
export type SharedPreviewKind = 'office' | 'image' | 'pdf' | 'video' | 'webapp' | 'markdown' | 'csv' | 'text' | 'none'

/** The kinds the public page can show inline, and how (Adam, 14 Sep: every
 *  kind of shared artifact renders). A web app or a single HTML page is
 *  framed from the link's own app route; Office through Microsoft's viewer;
 *  PDF, image and video natively; markdown rendered; CSV as a table; JSON,
 *  code and plain text as text. Only a zip or an unknown binary is download only. */
export function sharedArtifactPreviewKind(mime: string, fileName: string, artifactType?: string): SharedPreviewKind {
  const lower = fileName.toLowerCase()
  if (artifactType === 'webapp' || /\.html?$/.test(lower)) return 'webapp'
  if (mime.startsWith('video/') || /\.(mp4|webm|mov)$/.test(lower)) return 'video'
  if (/\.(pptx?|docx?|xlsx?)$/.test(lower)) return 'office'
  if (mime.startsWith('image/') || /\.(png|jpe?g|gif|webp|svg)$/.test(lower)) return 'image'
  if (mime === 'application/pdf' || lower.endsWith('.pdf')) return 'pdf'
  if (/\.(md|markdown)$/.test(lower)) return 'markdown'
  if (/\.(csv|tsv)$/.test(lower)) return 'csv'
  if (mime.startsWith('text/') || mime === 'application/json' || /\.(json|txt|js|ts|py|r|sql|sh|css|yaml|yml|xml|log)$/.test(lower)) return 'text'
  return 'none'
}

export function formatFileSize(bytes: number): string {
  if (!bytes || bytes <= 0) return ''
  if (bytes < 1024) return `${bytes} bytes`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
